import React, { useState, useEffect } from 'react';
import { app } from '../firebase'; // Importez votre configuration Firebase
import { getFirestore, collection, onSnapshot, query, where, getDoc, doc } from 'firebase/firestore'; 
import { useUser } from '../contexts/UserContext';
import DeleteModal from '../components/modals/DeleteModal';
import AddEvent from '../events/AddEvent';
import moment from 'moment';
import { addEventButton, archiveEvent, displayCalendar, displayEvents } from './CalendarFunctions';
import UpdateEvent from '../events/UpdateEvent';
import DetailsEvent from '../events/DetailsEvent';

const db = getFirestore(app);

const CustomCalendarProvider = ({listCoownership}) => {

  const [events, setEvents] = useState([]); 
  const [loader, setLoader] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false); 
  const [showArchiveModal, setShowArchiveModal] = useState(false); 
  const [elementToArchive, setElementToArchive] = useState(null); 
  const [showUpdateModal, setShowUpdateModal] = useState(false); 
  const [elementToUpdate, setElementToUpdate] = useState(null); 
  const [dateCalendar, setDateCalendar] = useState(new Date()); 
  const [dateBegining, setDateBegining] = useState(new Date()); 
  const [dateEnd, setDateEnd] = useState(() => {
    const nextDay = new Date(dateBegining); // Clone `dateBegining`
    nextDay.setDate(nextDay.getDate() + 1); // Incrémente le jour de 1
    return nextDay;
  });

  const [allEvents, setAllEvents] = useState([]); // [{ "YYYY-MM-DD": [events] }]
  const [eventsDate, setEventsDate] = useState([]);
  const [unsubscribe, setUnsubscribe] = useState(null);

  //contexte de l'utilisateur connecté
  const user = useUser();  

  const [selectedEvent, setSelectedEvent] = useState(null);

  const getMonthDateRange = (date) => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startOfMonth, endOfMonth };
  };

  useEffect(() => {
    setupEventsListener();
  }, [])

  const handleMonthChange = (newDate) => {
    setupEventsListener(newDate);
  };

  const setupEventsListener = (date = dateCalendar) => {
    setEventsDate([]); //on remet les dates à vide pour insérer toutes les dates
    setLoader(true);

    const { startOfMonth, endOfMonth } = getMonthDateRange(date);

    // Créer la requête
    const queryPlanning = query(
      collection(db, "users/" + user.user.user?.uid + "/events"),
      where('status', '==', 'accepted'),
      where('dateBegining', '>=', startOfMonth),
      where('dateBegining', '<=', endOfMonth)
    );

    // Se désabonner de l'ancien listener s'il existe
    if (unsubscribe) {
      unsubscribe();
    }

    // Créer un nouveau snapshot listener
    const newUnsubscribe = onSnapshot(queryPlanning, (snapshot) => {
      // Objets temporaires pour stocker les événements
      const tempEventsMap = [];
      const allEventDates = []; // Tableau temporaire pour collecter toutes les dates

      snapshot.forEach((doc) => {
        const event = doc.data();
        const eventId = doc.id;
        // Convertir le timestamp en millisecondes (Firestore utilise des millisecondes)
        const eventDate = new Date(event.dateBegining.seconds * 1000);
        const dateKey = moment(eventDate).format('YYYY-MM-DD');
        
        // Ajouter la date à notre tableau temporaire
        if (!allEventDates.includes(dateKey)) {
          allEventDates.push(dateKey);
        }

        // Créer un objet d'événement
        const eventObj = {
          coownership: event.coownership.path,
          id: eventId,
          idCreator: event.idCreator.path,
          title: event.title,
          description: event.description,
          firstname: event.firstnameCreator,
          lastname: event.lastnameCreator,
          dateBegining: event.dateBegining.seconds,
          dateEnd: event.dateEnd.seconds,
          status: event.status,
          files: event.files,
          multipleDates: event?.multipleDates ?? false, 
          createdByProvider: event?.createdByProvider ?? false,
          coownershipComiteeTarget: event?.coownershipComiteeTarget ?? undefined,
          coownerTarget: event?.coownerTarget ?? undefined,
          tenantTarget: event?.tenantTarget ?? undefined,
        };

        // Ajouter à la map d'événements par date
        if (!tempEventsMap[dateKey]) {
          tempEventsMap[dateKey] = [];
        }
        
        tempEventsMap[dateKey].push(eventObj);
      });

      setAllEvents(tempEventsMap); // Mettre à jour les états avec toutes les données
      setEventsDate(allEventDates); // Mise à jour unique de eventsDate
      setLoader(false);
    }, (error) => {
      console.error("Erreur lors de l'écoute des événements:", error);
      setLoader(false);
    });

    // Stocker la fonction de désinscription
    setUnsubscribe(() => newUnsubscribe);
  };
  
  // Mettre à jour les événements actuels lorsque la date change
  useEffect(() => {

    const setEventsFromDate = async () => {
      const dateKey = moment(dateBegining).format('YYYY-MM-DD');
    
      if (allEvents[dateKey]) {
  
        // Créer une copie des événements pour pouvoir les modifier
        const updatedEvents = [...allEvents[dateKey]];
        
        // Pour chaque événement, récupérer les informations de la copropriété
        for (let i = 0; i < updatedEvents.length; ++i) {
          try {
            // Récupérer le document de la copropriété
            const coownershipRef = doc(db, updatedEvents[i].coownership);
            const coownershipSnap = await getDoc(coownershipRef);
            
            if (coownershipSnap.exists()) {
              const coownershipData = coownershipSnap.data();
              
              // Ajouter les champs image et name de la copropriété à l'événement
              updatedEvents[i] = {
                ...updatedEvents[i],
                imageCoownership: coownershipData.image || '',
                nameCoownership: coownershipData.name || ''
              };
            }
          } catch (error) {
            console.error('Erreur lors de la récupération de la copropriété:', error);
          }
        }
  
        setEvents(updatedEvents);
      } else {
        setEvents([]);
      }
    }
    
    setEventsFromDate(); 
  }, [dateBegining, allEvents]);


  useEffect(() => {
    if(elementToArchive !== null){
      setShowArchiveModal(true); 
    }
  }, [elementToArchive]);

  useEffect(() => {
    if(showArchiveModal === false){
      setElementToArchive(null);
    }
  }, [showArchiveModal]);

  useEffect(() => {
    if(elementToUpdate !== null){
      setShowUpdateModal(true); 
    }
  }, [elementToUpdate]);

  useEffect(() => {
    if(showUpdateModal === false){
      setElementToUpdate(null);
    }
  }, [showUpdateModal]);

  if(selectedEvent){
    return (
      <DetailsEvent event={selectedEvent} onBack={() => setSelectedEvent(null)} />
    )
  }

  return (
    <div>  
      <div className='flex gap-4 justify-end'>
        {addEventButton(dateBegining, setShowAddEvent)} 
      </div>
      
      {displayCalendar(dateCalendar, setDateCalendar, setDateBegining, setDateEnd, eventsDate, handleMonthChange)}

      {displayEvents(user, loader, events, dateBegining, setElementToArchive, true, setElementToUpdate, setSelectedEvent)}

      {showAddEvent ? (
        <AddEvent onClose={() => setShowAddEvent(false)} date={dateBegining} listCoownership={listCoownership}/>
      ) : null }

      {showUpdateModal ? (
        <UpdateEvent onClose={() => setShowUpdateModal(false)} eventPath={elementToUpdate}/>
      ) : null }

      {showArchiveModal ? (
        <DeleteModal closeAction={() => setShowArchiveModal(false)} confirmAction={() => archiveEvent(elementToArchive, setShowArchiveModal)} type={"archive"}/>
      ) : null }
    </div>
  );
};


export default CustomCalendarProvider;

